<template>
  <div class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" @click="$emit('close')">
    <div class="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl w-full h-full flex flex-col overflow-hidden" @click.stop>
      <!-- Header -->
      <div class="flex items-center justify-between px-6 py-4 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900">
        <div class="flex items-center space-x-3 flex-1 min-w-0">
          <div :class="iconBgClass" class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
            <component :is="fileIcon" class="w-5 h-5 text-white" />
          </div>
          <div class="min-w-0 flex-1">
            <h2 class="text-lg font-semibold text-secondary-900 dark:text-white truncate">{{ attachment.name }}</h2>
            <p class="text-sm text-secondary-500 dark:text-secondary-400">{{ formatSize(attachment.size) }} • {{ fileExtension.toUpperCase() }}</p>
          </div>
        </div>
        <div class="flex items-center space-x-2 ml-4">
          <!-- Zoom controls for images -->
          <div v-if="isImage && previewUrl" class="flex items-center space-x-1 mr-2 border-r border-secondary-300 dark:border-secondary-600 pr-2">
            <button
              @click="zoomOut"
              class="p-2 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors"
              title="Alejar"
              :disabled="zoom <= 50"
            >
              <ZoomOut class="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
            <span class="text-sm text-secondary-600 dark:text-secondary-400 w-12 text-center">{{ zoom }}%</span>
            <button
              @click="zoomIn"
              class="p-2 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors"
              title="Acercar"
              :disabled="zoom >= 200"
            >
              <ZoomIn class="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
            <button
              @click="resetZoom"
              class="p-2 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors"
              title="Restablecer"
            >
              <Maximize2 class="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
          </div>

          <!-- Responder/Reenviar desde el correo adjunto: solo en la rama .eml -->
          <div v-if="isEmail && emlData && !readOnly" class="flex items-center space-x-1 mr-2 border-r border-secondary-300 dark:border-secondary-600 pr-2">
            <button
              type="button"
              @click="respond('reply')"
              class="p-2 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors disabled:opacity-50"
              :title="emailAuthStore.isAuthenticated ? t('emailWorkspace.commands.reply') : t('emailComposer.embedded.notConnected')"
              :disabled="!emailAuthStore.isAuthenticated || busyAction !== null"
            >
              <Loader2 v-if="busyAction === 'reply'" class="w-5 h-5 text-secondary-600 dark:text-secondary-400 animate-spin" />
              <Reply v-else class="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
            <button
              type="button"
              @click="respond('replyAll')"
              class="p-2 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors disabled:opacity-50"
              :title="emailAuthStore.isAuthenticated ? t('emailWorkspace.commands.replyAll') : t('emailComposer.embedded.notConnected')"
              :disabled="!emailAuthStore.isAuthenticated || busyAction !== null"
            >
              <Loader2 v-if="busyAction === 'replyAll'" class="w-5 h-5 text-secondary-600 dark:text-secondary-400 animate-spin" />
              <ReplyAll v-else class="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
            <button
              type="button"
              @click="respond('forward')"
              class="p-2 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors disabled:opacity-50"
              :title="emailAuthStore.isAuthenticated ? t('emailWorkspace.commands.forward') : t('emailComposer.embedded.notConnected')"
              :disabled="!emailAuthStore.isAuthenticated || busyAction !== null"
            >
              <Loader2 v-if="busyAction === 'forward'" class="w-5 h-5 text-secondary-600 dark:text-secondary-400 animate-spin" />
              <Forward v-else class="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
            </button>
          </div>

          <button
            @click="handleDownload"
            class="p-2 hover:bg-primary-100 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            title="Descargar"
          >
            <Download class="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </button>
          <button
            @click="$emit('close')"
            class="p-2 hover:bg-secondary-200 dark:hover:bg-secondary-700 rounded-lg transition-colors"
            title="Cerrar (Esc)"
          >
            <X class="w-5 h-5 text-secondary-600 dark:text-secondary-400" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-auto bg-secondary-100 dark:bg-secondary-950">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex items-center justify-center h-full">
          <div class="text-center">
            <div class="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
            <p class="text-secondary-600 dark:text-secondary-400 font-medium">Cargando vista previa...</p>
          </div>
        </div>

        <!-- Image Preview -->
        <div v-else-if="isImage && previewUrl" class="flex items-center justify-center h-full p-8">
          <img
            :src="previewUrl"
            :alt="attachment.name"
            :style="{ transform: `scale(${zoom / 100})` }"
            class="max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-transform duration-200"
          />
        </div>

        <!-- PDF Preview -->
        <div v-else-if="isPdf && previewUrl" class="h-full w-full flex flex-col">
          <!-- Barra de herramientas para PDF -->
          <div class="bg-secondary-100 dark:bg-secondary-900 px-4 py-2 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
            <span class="text-sm text-secondary-600 dark:text-secondary-400">Vista previa del PDF</span>
            <button
              @click="openInNewTab"
              class="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors flex items-center space-x-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              <span>Abrir en nueva pestaña</span>
            </button>
          </div>
          
          <!-- Visor de PDF -->
          <div class="flex-1 relative bg-secondary-100 dark:bg-secondary-950">
            <iframe
              v-if="pdfBlobUrl"
              :src="pdfBlobUrl"
              class="w-full h-full border-0"
              title="PDF Preview"
            ></iframe>
            <div v-else class="flex items-center justify-center h-full">
              <div class="text-center">
                <div class="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-600 mx-auto mb-4"></div>
                <p class="text-secondary-600 dark:text-secondary-400 font-medium">Cargando PDF...</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Excel Preview -->
        <ExcelSheetViewer v-else-if="isExcel" :load-buffer="fetchAttachmentBuffer" />

        <!-- Correo adjunto (.eml) -->
        <div v-else-if="isEmail" class="h-full overflow-auto bg-white dark:bg-secondary-800">
          <!-- Loading -->
          <div v-if="isLoadingEml" class="flex items-center justify-center py-16">
            <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            <span class="ml-3 text-sm text-secondary-500 dark:text-secondary-400">Cargando el correo...</span>
          </div>

          <!-- Error -->
          <div v-else-if="emlError" class="flex flex-col items-center justify-center py-16 text-center">
            <Mail class="h-16 w-16 text-red-400 mb-4" />
            <p class="text-sm text-red-600 dark:text-red-400">{{ emlError }}</p>
          </div>

          <!-- Correo -->
          <div v-else-if="emlData" class="p-6 space-y-4">
            <div class="pb-4 border-b border-secondary-200 dark:border-secondary-700">
              <h3 class="text-base font-semibold text-secondary-900 dark:text-white mb-3">
                {{ emlData.subject || '(Sin asunto)' }}
              </h3>
              <dl class="text-sm space-y-1">
                <div class="flex gap-2">
                  <dt class="text-secondary-500 dark:text-secondary-400 w-16 flex-shrink-0">De</dt>
                  <dd class="text-secondary-700 dark:text-secondary-200 min-w-0 break-words">
                    <span v-if="emlData.fromName" class="font-medium">{{ emlData.fromName }}</span>
                    <span class="text-secondary-500 dark:text-secondary-400">{{ emlData.fromName ? ` <${emlData.fromAddress}>` : emlData.fromAddress }}</span>
                  </dd>
                </div>
                <div v-if="emlData.to.length" class="flex gap-2">
                  <dt class="text-secondary-500 dark:text-secondary-400 w-16 flex-shrink-0">Para</dt>
                  <dd class="text-secondary-700 dark:text-secondary-200 min-w-0 break-words">{{ emlData.to.join(', ') }}</dd>
                </div>
                <div v-if="emlData.cc.length" class="flex gap-2">
                  <dt class="text-secondary-500 dark:text-secondary-400 w-16 flex-shrink-0">CC</dt>
                  <dd class="text-secondary-700 dark:text-secondary-200 min-w-0 break-words">{{ emlData.cc.join(', ') }}</dd>
                </div>
                <div v-if="emlData.date" class="flex gap-2">
                  <dt class="text-secondary-500 dark:text-secondary-400 w-16 flex-shrink-0">Fecha</dt>
                  <dd class="text-secondary-700 dark:text-secondary-200">{{ formatFullDate(emlData.date) }}</dd>
                </div>
              </dl>
            </div>

            <!-- Adjuntos del correo anidado: se descargan de lo ya parseado en memoria -->
            <div v-if="emlData.attachments.length" class="flex flex-wrap gap-2">
              <button
                v-for="(att, idx) in emlData.attachments"
                :key="idx"
                type="button"
                @click="downloadNestedAttachment(idx)"
                class="flex items-center gap-2 px-3 py-2 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:border-primary-400 dark:hover:border-primary-500 transition-colors text-left"
              >
                <Paperclip class="w-4 h-4 text-secondary-400 flex-shrink-0" />
                <span class="min-w-0">
                  <span class="block text-xs font-medium text-secondary-800 dark:text-secondary-100 truncate max-w-[16rem]">{{ att.filename }}</span>
                  <span class="block text-[11px] text-secondary-400">{{ formatSize(att.size) }}</span>
                </span>
                <Download class="w-4 h-4 text-secondary-400 flex-shrink-0" />
              </button>
            </div>

            <!-- Mismo iframe y misma sanitización que el cuerpo de un correo normal -->
            <EmailBodyIframe :html-content="emlData.html" :attachments="emlInlineRefs" />
          </div>
        </div>

        <!-- No Preview Available -->
        <div v-else class="flex flex-col items-center justify-center h-full text-center p-8">
          <div :class="iconBgClass" class="w-24 h-24 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
            <component :is="fileIcon" class="w-12 h-12 text-white" />
          </div>
          <p class="text-xl font-semibold text-secondary-900 dark:text-white mb-2">Vista previa no disponible</p>
          <p class="text-sm text-secondary-500 dark:text-secondary-400 mb-8 max-w-md">Este tipo de archivo no puede ser visualizado en el navegador. Descárgalo para abrirlo con la aplicación correspondiente.</p>
          <button
            @click="handleDownload"
            class="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-3 shadow-lg dark:bg-primary-500 dark:hover:bg-primary-600"
          >
            <Download class="w-5 h-5" />
            <span class="font-medium">Descargar archivo</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { File, Download, X, FileText, FileSpreadsheet, FileImage, FilePieChart, FileArchive, FileVideo, FileAudio, ZoomIn, ZoomOut, Maximize2, Mail, Paperclip, Reply, ReplyAll, Forward, Loader2 } from 'lucide-vue-next';
import type { EmailAttachment } from '@/types';
import { attachmentService } from '@/services';
import { formatFullDate } from '@/utils';
import EmailBodyIframe from './EmailBodyIframe.vue';
import ExcelSheetViewer from '@/components/documents/ExcelSheetViewer.vue';
import type { ParsedEml } from '@/utils/emlPreview';
import { useEmailAuthStore } from '@/stores';
import { useEmbeddedEmailReply } from '@/composables/useEmbeddedEmailReply';
import type { EmailAttachContext } from '@/composables/useEmailAsAttachment';
import type { EmbeddedAction } from '@/utils/embeddedEmailReply';

const props = defineProps<{
  attachment: EmailAttachment;
  emailId: string;
  composerContext?: EmailAttachContext;
  readOnly?: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();
const emailAuthStore = useEmailAuthStore();
const { busyAction, respondToEmbedded } = useEmbeddedEmailReply();

function respond(action: EmbeddedAction) {
  if (emlData.value) respondToEmbedded(emlData.value, action, props.composerContext);
}

const previewUrl = ref<string | null>(null);
const pdfBlobUrl = ref<string | null>(null);
const isLoading = ref(false);
const zoom = ref(100);

/**
 * Las URLs de Storage (`downloadUrl`) llevan su propio token en la query y NO
 * deben recibir `Authorization`: con esa cabecera el navegador hace preflight
 * CORS y Storage rechaza el Bearer del backend → el PDF/Excel no cargaba. Solo
 * las URLs del backend (blob: o VITE_API_URL) necesitan el JWT.
 */
function fetchAttachment(url: string): Promise<Response> {
  const apiBase = String(import.meta.env.VITE_API_URL || '')
  const isBackendUrl = apiBase && url.startsWith(apiBase)
  return isBackendUrl
    ? fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } })
    : fetch(url)
}

const emlData = ref<ParsedEml | null>(null)
const isLoadingEml = ref(false)
const emlError = ref<string | null>(null)
/** Imágenes inline del correo anidado, en la forma que espera resolveCidImages. */
const emlInlineRefs = ref<Array<{ contentId?: string; url: string }>>([])
const emlObjectUrls: string[] = []

/** El binario del adjunto, venga de Storage o del backend. */
async function fetchAttachmentBuffer(): Promise<ArrayBuffer> {
  const url = props.attachment.downloadUrl ?? props.attachment.url
  if (url) return (await fetchAttachment(url)).arrayBuffer()

  const blob = await attachmentService.downloadAttachment(props.emailId, props.attachment.id)
  if (!blob) throw new Error('No se pudo descargar el adjunto')
  return blob.arrayBuffer()
}

const isImage = computed(() => attachmentService.isImageFile(props.attachment.contentType));
const isPdf = computed(() => attachmentService.isPdfFile(props.attachment.contentType));

const isExcel = computed(() =>
  attachmentService.isExcelFile(props.attachment.contentType, props.attachment.name)
)

const isEmail = computed(() =>
  attachmentService.isEmailFile(props.attachment.contentType, props.attachment.name)
)

const fileIcon = computed(() => {
  const contentType = props.attachment.contentType.toLowerCase();

  if (isEmail.value) return Mail;
  if (contentType.includes('image')) return FileImage;
  if (contentType.includes('pdf')) return FileText;
  if (contentType.includes('word') || contentType.includes('document')) return FileText;
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return FileSpreadsheet;
  if (contentType.includes('powerpoint') || contentType.includes('presentation')) return FilePieChart;
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('compressed')) return FileArchive;
  if (contentType.includes('video')) return FileVideo;
  if (contentType.includes('audio')) return FileAudio;
  
  return File;
});

const iconBgClass = computed(() => {
  const contentType = props.attachment.contentType.toLowerCase();

  if (isEmail.value) return 'bg-gradient-to-br from-slate-500 to-slate-600';
  if (contentType.includes('image')) return 'bg-gradient-to-br from-purple-500 to-purple-600';
  if (contentType.includes('pdf')) return 'bg-gradient-to-br from-red-500 to-red-600';
  if (contentType.includes('word') || contentType.includes('document')) return 'bg-gradient-to-br from-blue-500 to-blue-600';
  if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'bg-gradient-to-br from-green-500 to-green-600';
  if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'bg-gradient-to-br from-orange-500 to-orange-600';
  if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('compressed')) return 'bg-gradient-to-br from-yellow-500 to-yellow-600';
  if (contentType.includes('video')) return 'bg-gradient-to-br from-pink-500 to-pink-600';
  if (contentType.includes('audio')) return 'bg-gradient-to-br from-indigo-500 to-indigo-600';
  
  return 'bg-gradient-to-br from-secondary-500 to-secondary-600';
});

const fileExtension = computed(() => {
  const name = props.attachment.name;
  const lastDot = name.lastIndexOf('.');
  return lastDot !== -1 ? name.substring(lastDot + 1) : 'file';
});

async function loadEmailPreview() {
  isLoadingEml.value = true
  emlError.value = null
  try {
    const { parseEml } = await import('@/utils/emlPreview')
    const parsed = await parseEml(await fetchAttachmentBuffer())

    // Las imágenes incrustadas solo se pueden mostrar desde memoria: no están
    // en Storage, viven dentro del .eml.
    emlInlineRefs.value = parsed.inlineImages.map(img => {
      const url = window.URL.createObjectURL(new Blob([img.content], { type: img.mimeType }))
      emlObjectUrls.push(url)
      return { contentId: img.contentId, url }
    })

    emlData.value = parsed
  } catch {
    emlError.value = 'No se pudo abrir el correo adjunto'
    emlData.value = null
  } finally {
    isLoadingEml.value = false
  }
}

function downloadNestedAttachment(index: number) {
  const att = emlData.value?.attachments[index]
  if (!att) return
  attachmentService.downloadFile(new Blob([att.content], { type: att.mimeType }), att.filename)
}

function zoomIn() {
  if (zoom.value < 200) {
    zoom.value = Math.min(200, zoom.value + 25);
  }
}

function zoomOut() {
  if (zoom.value > 50) {
    zoom.value = Math.max(50, zoom.value - 25);
  }
}

function resetZoom() {
  zoom.value = 100;
}

function handleKeydown(event: KeyboardEvent) {
  // Escribir en el compositor (Esc para cerrar un desplegable, +/-/0 al
  // teclear) no debe cerrar el visor ni hacer zoom: el muelle de compositores
  // queda por encima, así que el usuario puede consultar el adjunto mientras escribe.
  const target = event.target as HTMLElement | null;
  if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;

  if (event.key === 'Escape') {
    emit('close');
  } else if (event.key === '+' || event.key === '=') {
    zoomIn();
  } else if (event.key === '-') {
    zoomOut();
  } else if (event.key === '0') {
    resetZoom();
  }
}

onMounted(async () => {
  if (isImage.value || isPdf.value) {
    isLoading.value = true

    // La bandeja entrega la copia de Storage en `url`; sin mirarla, el visor
    // iba al backend, que responde 401 a quien no tiene su buzón conectado.
    const directUrl = props.attachment.downloadUrl ?? props.attachment.url
    if (directUrl) {
      previewUrl.value = directUrl
    } else {
      previewUrl.value = await attachmentService.getAttachmentBlobUrl(props.emailId, props.attachment.id)
    }

    if (isPdf.value && previewUrl.value) {
      try {
        const response = await fetchAttachment(previewUrl.value)
        const blob = await response.blob()
        const pdfBlob = new Blob([blob], { type: 'application/pdf' })
        pdfBlobUrl.value = window.URL.createObjectURL(pdfBlob)
      } catch (error) {
        console.error('Error creating PDF blob:', error)
      }
    }

    isLoading.value = false
  } else if (isEmail.value) {
    await loadEmailPreview()
  }

  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  if (previewUrl.value) {
    window.URL.revokeObjectURL(previewUrl.value);
  }
  
  if (pdfBlobUrl.value) {
    window.URL.revokeObjectURL(pdfBlobUrl.value);
  }

  for (const url of emlObjectUrls) {
    window.URL.revokeObjectURL(url);
  }

  window.removeEventListener('keydown', handleKeydown);
});

function formatSize(bytes: number): string {
  return attachmentService.formatFileSize(bytes);
}

async function handleDownload() {
  const directUrl = props.attachment.downloadUrl ?? props.attachment.url
  if (directUrl) {
    const response = await fetchAttachment(directUrl)
    const blob = await response.blob()
    attachmentService.downloadFile(blob, props.attachment.name)
  } else {
    const blob = await attachmentService.downloadAttachment(props.emailId, props.attachment.id)
    if (blob) {
      attachmentService.downloadFile(blob, props.attachment.name)
    }
  }
}

async function openInNewTab() {
  if (previewUrl.value) {
    // Para PDFs, crear un nuevo blob con tipo application/pdf para asegurar que se abra en el navegador
    if (isPdf.value) {
      try {
        const response = await fetchAttachment(previewUrl.value);
        const blob = await response.blob();
        // Crear un nuevo blob con el tipo correcto
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(pdfBlob);
        window.open(url, '_blank');
        // Limpiar el URL después de un tiempo
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (error) {
        console.error('Error opening PDF:', error);
        // Fallback: intentar abrir directamente
        window.open(previewUrl.value, '_blank');
      }
    } else {
      window.open(previewUrl.value, '_blank');
    }
  }
}
</script>
